import React, { useState } from "react";
import { Box, Tooltip, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { CustomerCompanyDetails } from "../models/models";
import { ProfileEditForm } from "./Company";
import { CompanyGroup } from "../../companyGroups/models/models";
import { RoleCode } from "../../shared/models/customerModels";
import CompanyLogo from "../../sentOffers/components/create/details/CompanyLogo";
import Location from "../../../shared/components/Location";
import DefaultCompanyLogo from "./DefaultCompanyLogo";
import TierBadge from "../../partnerships/components/TierBadge";
import { RelationshipTierCode } from "../../partnerships/models/models";
import { tierStyleMap } from "../../partnerships/utils/tierMaps";
import { CustomerReviewSummary } from "../../servicing/models/reviewModels";
import AddressAutocomplete, { AddressData } from "../../../shared/components/AddressAutocomplete";
import useTranslation from "../../../shared/hooks/useTranslation";
import dayjs from "dayjs";

import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import FacebookOutlinedIcon from "@mui/icons-material/FacebookOutlined";
import InstagramIcon from "@mui/icons-material/Instagram";
import XIcon from "@mui/icons-material/X";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import StarOutlineIcon from "@mui/icons-material/StarOutline";

import { dt } from "../../../shared/themes/redesignTokens";

// =====================================================================
//  Types
// =====================================================================

interface PartnershipStatusInfo {
  type: "partner" | "pending" | "none";
  tier?: RelationshipTierCode;
  activatedAt?: string;
  partnershipId?: string;
}

interface CompanyHeroProps {
  company: CustomerCompanyDetails;
  isOwnProfile?: boolean;
  isEditing?: boolean;
  editForm?: ProfileEditForm;
  onFieldChange?: (field: keyof ProfileEditForm, value: any) => void;
  onEditToggle?: () => void;
  onSave?: () => void;
  saving?: boolean;
  saveDropdown?: React.ReactNode;
  onLogoChange?: () => void;
  groups?: CompanyGroup[];
  companyId?: string;
  partnershipStatus?: PartnershipStatusInfo;
  onInvitePartner?: () => void;
  reviewSummary?: CustomerReviewSummary | null;
}

// =====================================================================
//  Local constants
// =====================================================================

const green = dt.roleGreen;
const greenSoft = dt.roleGreenBg;
const purple = dt.rolePurple;
const purpleSoft = dt.rolePurpleBg;
const borderLight = dt.rowBorder;

// Distance pill: soft green (distinct from amber group badges)
const distanceGreen = "#10B981";
const distanceGreenBg = "rgba(16, 185, 129, 0.08)";

const inputStyle: React.CSSProperties = {
  width: "100%",
  border: `1px solid ${dt.cardBorder}`,
  borderRadius: dt.radiusSm,
  padding: "8px 12px",
  fontSize: "13px",
  fontFamily: dt.font,
  color: dt.textPrimary,
  outline: "none",
  background: "#F8FAFC",
  boxSizing: "border-box",
};

const btnBase = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  padding: "9px 16px",
  borderRadius: dt.radiusSm,
  fontSize: "13px",
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: dt.font,
  transition: "all .2s ease",
  whiteSpace: "nowrap" as const,
  border: `1px solid ${dt.cardBorder}`,
  background: dt.cardBg,
  color: dt.textSecondary,
};

// Compact button for action buttons (Save, Partnership) in top-right
const btnCompact = {
  display: "flex",
  alignItems: "center",
  gap: "4px",
  padding: "5px 12px",
  borderRadius: dt.radiusSm,
  fontSize: "12px",
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: dt.font,
  transition: "all .2s ease",
  whiteSpace: "nowrap" as const,
  border: `1px solid ${dt.cardBorder}`,
  background: dt.cardBg,
  color: dt.textSecondary,
  height: "32px",
  boxSizing: "border-box" as const,
};

const anim = "fadeUp 0.4s cubic-bezier(0.4,0,0.2,1)";


// =====================================================================
//  Component
// =====================================================================

const CompanyHero = ({
  company,
  isOwnProfile = false,
  isEditing = false,
  editForm,
  onFieldChange,
  onEditToggle,
  onSave,
  saving = false,
  saveDropdown,
  onLogoChange,
  groups,
  companyId,
  partnershipStatus,
  onInvitePartner,
  reviewSummary,
}: CompanyHeroProps) => {
  const navigate = useNavigate();
  const { translate } = useTranslation(["partnerHub", "common", "partnerships"]);
  const isPhone = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(max-width:1300px)");
  const isDesktop = !isTablet;

  const [contactExpanded, setContactExpanded] = useState(false);

  const isSupplier = company.roleCode === RoleCode.supplier;
  const roleLabel = isSupplier
    ? translate("partnerHub:profile.supplier")
    : translate("partnerHub:profile.buyer");
  const roleColor = isSupplier ? green : purple;
  const roleBg = isSupplier ? greenSoft : purpleSoft;

  // --- Contact info rows ---
  const addressStr = [
    company.address?.addressLine,
    [company.address?.city, company.address?.zipCode].filter(Boolean).join(" "),
    company.address?.countryName,
  ]
    .filter(Boolean)
    .join(", ");

  const allContactRows: {
    icon: React.ReactNode;
    label: string;
    value: string | null;
    href?: string;
    editField?: keyof ProfileEditForm;
    isAddress?: boolean;
  }[] = [
    {
      icon: <PlaceOutlinedIcon sx={{ fontSize: 15, color: dt.textMuted }} />,
      label: translate("partnerHub:details.address"),
      value: addressStr || null,
      isAddress: true,
    },
    {
      icon: <PhoneOutlinedIcon sx={{ fontSize: 15, color: dt.textMuted }} />,
      label: translate("partnerHub:details.phone"),
      value: company.phone || null,
      href: company.phone ? `tel:${company.phone}` : undefined,
      editField: "phone",
    },
    {
      icon: <MailOutlinedIcon sx={{ fontSize: 15, color: dt.textMuted }} />,
      label: translate("partnerHub:details.email"),
      value: company.email || null,
      href: company.email ? `mailto:${company.email}` : undefined,
      editField: "email",
    },
  ];

  // Website row
  if (company.website || isEditing) {
    allContactRows.push({
      icon: <OpenInNewOutlinedIcon sx={{ fontSize: 15, color: dt.textMuted }} />,
      label: translate("partnerHub:details.website"),
      value: company.website || null,
      href: company.website || undefined,
      editField: "website",
    });
  }

  // Hide empty rows when not editing
  const contactRows = isEditing
    ? allContactRows
    : allContactRows.filter((row) => !!row.value);

  // --- Social links ---
  const socialLinks: {
    icon: React.ReactNode;
    value: string | null;
    href?: string;
    editField?: keyof ProfileEditForm;
    prefix?: string;
    placeholder?: string;
    tooltip: string;
  }[] = [
    {
      icon: <LinkedInIcon sx={{ fontSize: 18, color: "#0A66C2" }} />,
      value: company.linkedInLink || null,
      href: company.linkedInLink ? `https://linkedin.com/in/${company.linkedInLink}` : undefined,
      editField: "linkedInLink",
      prefix: "linkedin.com/in/",
      placeholder: "username",
      tooltip: translate("partnerHub:details.linkedIn"),
    },
    {
      icon: <FacebookOutlinedIcon sx={{ fontSize: 18, color: "#1877F2" }} />,
      value: company.facebookLink || null,
      href: company.facebookLink ? `https://facebook.com/${company.facebookLink}` : undefined,
      editField: "facebookLink",
      prefix: "facebook.com/",
      placeholder: "page-or-username",
      tooltip: translate("partnerHub:details.facebook"),
    },
    {
      icon: <InstagramIcon sx={{ fontSize: 18, color: "#E4405F" }} />,
      value: company.instagramLink || null,
      href: company.instagramLink
        ? `https://instagram.com/${company.instagramLink}`
        : undefined,
      editField: "instagramLink",
      prefix: "instagram.com/",
      placeholder: "username",
      tooltip: translate("partnerHub:details.instagram"),
    },
    {
      icon: <XIcon sx={{ fontSize: 18, color: dt.textPrimary }} />,
      value: company.xLink || null,
      href: company.xLink ? `https://x.com/${company.xLink}` : undefined,
      editField: "xLink",
      prefix: "x.com/",
      placeholder: "handle",
      tooltip: "X",
    },
  ];

  const hasSocialLinks =
    isEditing ||
    socialLinks.some((l) => !!l.value);

  // --- Group badges (Tier 2) ---
  const matchingGroups = groups?.filter((g) => {
    const cId = companyId?.toLowerCase();
    return g.items?.some(
      (item) => item.customer?.id?.toLowerCase() === cId
    );
  });

  // --- Helpers ---

  const renderInfoRow = (
    row: typeof allContactRows[0],
    idx: number,
    isLast: boolean
  ) => (
    <Box
      key={idx}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        py: "7px",
        borderBottom: isLast ? "none" : `1px solid ${borderLight}`,
      }}
    >
      <Box sx={{ mt: "2px", flexShrink: 0 }}>{row.icon}</Box>
      {isEditing && row.isAddress && editForm && onFieldChange ? (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
          <Box
            sx={{
              "& .MuiTextField-root": {
                "& .MuiOutlinedInput-root": {
                  borderRadius: dt.radiusSm,
                  fontFamily: dt.font,
                  fontSize: "13px",
                  background: "#F8FAFC",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: dt.cardBorder,
                },
              },
            }}
          >
            <AddressAutocomplete
              placeholder={translate("partnerHub:contactInfo.addressLine")}
              defaultValue={{
                address: editForm.addressLine,
                city: editForm.city,
                zipCode: editForm.zipCode,
              }}
              onChange={(addressData?: AddressData) => {
                if (addressData) {
                  onFieldChange("addressLine", addressData.address || "");
                  onFieldChange("city", addressData.city || "");
                  onFieldChange("zipCode", addressData.zipCode || "");
                  if (addressData.latitude !== undefined)
                    onFieldChange("latitude", addressData.latitude);
                  if (addressData.longitude !== undefined)
                    onFieldChange("longitude", addressData.longitude);
                } else {
                  onFieldChange("addressLine", "");
                  onFieldChange("latitude", undefined);
                  onFieldChange("longitude", undefined);
                }
              }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: "6px" }}>
            <input
              value={editForm.city}
              onChange={(e) => onFieldChange("city", e.target.value)}
              placeholder={translate("partnerHub:contactInfo.city")}
              style={{ ...inputStyle, flex: 1 }}
            />
            <input
              value={editForm.zipCode}
              onChange={(e) => onFieldChange("zipCode", e.target.value)}
              placeholder={translate("partnerHub:contactInfo.zipCode")}
              style={{ ...inputStyle, width: "90px", flex: "none" }}
            />
          </Box>
          {company.address?.countryName && (
            <Box sx={{ fontSize: "12px", color: dt.textMuted, fontFamily: dt.font }}>
              {company.address.countryName}
            </Box>
          )}
        </Box>
      ) : isEditing && row.editField && editForm && onFieldChange ? (
        <input
          value={(editForm[row.editField] as string) || ""}
          onChange={(e) => onFieldChange(row.editField!, e.target.value)}
          placeholder={row.label}
          style={{ ...inputStyle, flex: 1 }}
        />
      ) : row.href && row.value ? (
        <Box
          component="a"
          href={row.href}
          target={row.href.startsWith("http") ? "_blank" : undefined}
          rel={row.href.startsWith("http") ? "noopener noreferrer" : undefined}
          sx={{
            fontSize: "13px",
            color: dt.textLink,
            textDecoration: "none",
            fontFamily: dt.font,
            wordBreak: "break-all",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {row.value}
        </Box>
      ) : (
        <Box
          sx={{
            fontSize: "13px",
            color: row.value ? dt.textPrimary : dt.textMuted,
            fontFamily: dt.font,
            lineHeight: 1.6,
          }}
        >
          {row.value || "\u2014"}
        </Box>
      )}
    </Box>
  );

  // --- Review stars renderer ---
  const renderStars = (rating: number) => {
    const stars: React.ReactNode[] = [];
    for (let i = 1; i <= 5; i++) {
      if (rating >= i) {
        stars.push(<StarIcon key={i} sx={{ fontSize: 16, color: dt.amber }} />);
      } else if (rating >= i - 0.5) {
        stars.push(<StarHalfIcon key={i} sx={{ fontSize: 16, color: dt.amber }} />);
      } else {
        stars.push(<StarOutlineIcon key={i} sx={{ fontSize: 16, color: "#D1D5DB" }} />);
      }
    }
    return stars;
  };

  // --- Collapsible section for phone view ---
  const CollapsibleSection = ({
    title,
    expanded,
    onToggle,
    children,
  }: {
    title: string;
    expanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
  }) => (
    <Box>
      <Box
        onClick={onToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          py: "10px",
          borderBottom: `1px solid ${borderLight}`,
        }}
      >
        <Box
          sx={{
            fontSize: "13px",
            fontWeight: 700,
            color: dt.textPrimary,
            fontFamily: dt.font,
          }}
        >
          {title}
        </Box>
        {expanded ? (
          <ExpandLessIcon sx={{ fontSize: 18, color: dt.textMuted }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 18, color: dt.textMuted }} />
        )}
      </Box>
      {expanded && <Box sx={{ pt: "4px" }}>{children}</Box>}
    </Box>
  );

  // --- Contact panel content (used in both desktop right column and mobile stacked) ---
  const ContactPanelContent = () => (
    <>
      {/* CONTACT INFO header */}
      <Box
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          color: dt.textMuted,
          textTransform: "uppercase",
          letterSpacing: "1px",
          fontFamily: dt.font,
          mb: "12px",
        }}
      >
        {translate("partnerHub:hero.contactInfo")}
      </Box>

      {/* Contact rows */}
      {contactRows.map((row, i) =>
        renderInfoRow(row, i, i === contactRows.length - 1)
      )}

      {/* Review summary slot */}
      {reviewSummary && reviewSummary.totalCount > 0 && (
        <>
          {/* Dashed divider */}
          <Box
            sx={{
              borderTop: `1px dashed ${dt.cardBorder}`,
              mt: "14px",
              pt: "14px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {renderStars(reviewSummary.averageRating)}
              </Box>
              <Box
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: dt.textPrimary,
                  fontFamily: dt.font,
                }}
              >
                {reviewSummary.averageRating.toFixed(1)}
              </Box>
              <Box
                sx={{
                  fontSize: "12px",
                  color: dt.textMuted,
                  fontFamily: dt.font,
                }}
              >
                ({reviewSummary.totalCount} {reviewSummary.totalCount === 1
                  ? translate("common:review")
                  : translate("common:reviews")})
              </Box>
            </Box>
          </Box>
        </>
      )}
    </>
  );

  // --- Action buttons (compact, top-right on desktop, inline on mobile) ---
  const ActionButtons = () => (
    <Box
      sx={{
        display: "flex",
        gap: "6px",
        flexWrap: "wrap",
        alignItems: "center",
        ...(isPhone
          ? { mt: "8px" }
          : {
              position: "absolute" as const,
              top: 16,
              right: isDesktop ? 16 : 20,
              flexDirection: "row" as const,
              zIndex: 2,
            }),
      }}
    >
      {!isEditing ? (
        <>
          {partnershipStatus?.type === "none" && !isOwnProfile && (
            <Box
              onClick={onInvitePartner}
              sx={{
                ...btnCompact,
                height: "32px",
                padding: "0 10px",
                fontSize: "12px",
                border: `1px solid ${dt.cardBorder}`,
                background: dt.cardBg,
                color: dt.blue,
                fontWeight: 600,
                "&:hover": {
                  background: dt.blueSoft,
                  borderColor: dt.blue,
                },
              }}
            >
              <HandshakeOutlinedIcon sx={{ fontSize: 14 }} />
              {translate("partnerships:invite.buttonShort") || "Invite"}
            </Box>
          )}
          {partnershipStatus?.type === "partner" &&
            !isOwnProfile &&
            partnershipStatus.tier && (
              <Box
                onClick={() => navigate("/partnerships")}
                sx={{
                  ...btnCompact,
                  height: "32px",
                  padding: "0 10px",
                  fontSize: "12px",
                  color: tierStyleMap[partnershipStatus.tier].text,
                  borderColor: tierStyleMap[partnershipStatus.tier].border,
                  "&:hover": {
                    backgroundColor:
                      tierStyleMap[partnershipStatus.tier].bg,
                  },
                }}
              >
                <HandshakeOutlinedIcon sx={{ fontSize: 14 }} />
                {translate("partnerships:partner.connectedButton")}
              </Box>
            )}
          {partnershipStatus?.type === "pending" && !isOwnProfile && (
            <Box
              sx={{
                ...btnCompact,
                height: "32px",
                padding: "0 10px",
                fontSize: "12px",
                opacity: 0.6,
                cursor: "default",
                borderColor: dt.blue + "40",
                color: dt.blue,
              }}
            >
              <HandshakeOutlinedIcon sx={{ fontSize: 14 }} />
              {translate("partnerships:invitation.pending")}
            </Box>
          )}
          {!isOwnProfile && saveDropdown}
          {isOwnProfile && (
            <Box
              onClick={onEditToggle}
              sx={{
                ...btnCompact,
                height: "32px",
                padding: "0 10px",
                fontSize: "12px",
                border: `1px solid ${dt.cardBorder}`,
                background: dt.cardBg,
                color: dt.blue,
                fontWeight: 600,
                "&:hover": {
                  background: dt.blueSoft,
                  borderColor: dt.blue,
                },
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 13 }} />
              {translate("partnerHub:edit.editProfile")}
            </Box>
          )}
        </>
      ) : (
        <>
          <Box onClick={onEditToggle} sx={{ ...btnCompact, height: "32px", padding: "0 10px", fontSize: "12px" }}>
            {translate("partnerHub:edit.cancel")}
          </Box>
          <Box
            onClick={saving ? undefined : onSave}
            sx={{
              ...btnCompact,
              height: "32px",
              padding: "0 10px",
              fontSize: "12px",
              background: green,
              border: "none",
              color: "#fff",
              fontWeight: 600,
              opacity: saving ? 0.7 : 1,
              cursor: saving ? "wait" : "pointer",
              "&:hover": { background: "#219A52" },
            }}
          >
            {translate("partnerHub:edit.saveChanges")}
          </Box>
        </>
      )}
    </Box>
  );

  // =====================================================================
  //  Render
  // =====================================================================

  return (
    <Box>
      {/* --- Breadcrumb --- */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          mb: 2.5,
          animation: `${anim} both`,
        }}
      >
        <Box
          onClick={() => navigate("/companies")}
          sx={{ ...btnBase, "&:hover": { borderColor: dt.blue, color: dt.blue } }}
        >
          <ArrowBackOutlinedIcon sx={{ fontSize: 15 }} />
          {translate("partnerHub:profile.back")}
        </Box>
        <Box
          sx={{
            width: "1px",
            height: "20px",
            background: dt.cardBorder,
            flexShrink: 0,
          }}
        />
        <Box
          sx={{
            fontSize: isPhone ? "16px" : "18px",
            fontWeight: 600,
            color: dt.textMuted,
            letterSpacing: "0.3px",
            fontFamily: dt.font,
          }}
        >
          {translate("partnerHub:profile.companyProfile") || "Company Profile"}
        </Box>
      </Box>

      {/* --- Unified Hero Card --- */}
      <Box
        sx={{
          background: dt.cardBg,
          borderRadius: dt.radiusLg,
          border: `1px solid ${dt.cardBorder}`,
          boxShadow: dt.cardShadow,
          overflow: "hidden",
          animation: `${anim} 0.08s both`,
        }}
      >
        {/* Gradient accent strip */}
        <Box
          sx={{
            height: "3px",
            borderRadius: `${dt.radiusLg} ${dt.radiusLg} 0 0`,
            background: `linear-gradient(90deg, ${dt.blue}, ${dt.blueHover}, ${dt.blue}00)`,
          }}
        />

        {/* --- Main content: two columns on desktop, stacked on mobile --- */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isDesktop ? "row" : "column",
          }}
        >
          {/* ===================== LEFT PANEL ===================== */}
          <Box
            sx={{
              flex: isDesktop ? "1 1 0" : "none",
              minWidth: 0,
              position: "relative",
            }}
          >
            {/* Action buttons (absolute top-right on desktop, inline on phone) */}
            {!isPhone && <ActionButtons />}

            {/* Row 1: Identity strip (Logo + Name + Tier1 badges) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: isPhone ? "14px" : "20px",
                padding: isPhone ? "20px 16px 0" : "24px 28px 0",
                // On desktop, give right padding for action buttons area
                pr: isPhone ? "16px" : isDesktop ? "120px" : "28px",
              }}
            >
              {/* Logo */}
              <Box
                onClick={isEditing ? onLogoChange : undefined}
                sx={{
                  width: isPhone ? 80 : isTablet ? 96 : 120,
                  height: isPhone ? 80 : isTablet ? 96 : 120,
                  borderRadius: dt.radius,
                  background: dt.pageBg,
                  border: `3px solid ${dt.cardBg}`,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  flexShrink: 0,
                  position: "relative",
                  cursor: isEditing ? "pointer" : "default",
                  transition: "box-shadow 0.2s ease",
                  "&:hover": isEditing
                    ? {
                        boxShadow: `0 0 0 3px ${dt.blueSoft}, 0 2px 12px rgba(0,0,0,0.08)`,
                      }
                    : {},
                }}
              >
                {company?.logo?.content ? (
                  <CompanyLogo
                    src={company.logo.content}
                    size={isPhone ? 74 : isTablet ? 90 : 114}
                  />
                ) : (
                  <DefaultCompanyLogo
                    seed={company.id || company.name}
                    size={isPhone ? 74 : isTablet ? 90 : 114}
                  />
                )}
                {isEditing && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: "2px",
                      background: "rgba(0,0,0,0.45)",
                      borderRadius: dt.radius,
                      transition: "background 0.2s",
                      "&:hover": { background: "rgba(0,0,0,0.55)" },
                    }}
                  >
                    <CameraAltOutlinedIcon sx={{ fontSize: 20, color: "#fff" }} />
                    <Box
                      sx={{
                        fontSize: "9px",
                        color: "rgba(255,255,255,0.9)",
                        fontWeight: 600,
                        fontFamily: dt.font,
                        letterSpacing: "0.03em",
                      }}
                    >
                      {translate("partnerHub:edit.changeLogo")}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Name + Tier 1 badges (location, distance, role) */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Company name — NO truncation, wraps freely */}
                <Box
                  sx={{
                    fontSize: isPhone ? "20px" : isTablet ? "22px" : "26px",
                    fontWeight: 800,
                    color: dt.textPrimary,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.25,
                    fontFamily: dt.font,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  {company.name}
                </Box>

                {/* Tier 1 badges: Location + Distance + Role */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    mt: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <Location
                    countryCode={company?.address?.countryId}
                    city={company?.address?.city}
                    textClassName="text-12-medium"
                    flagSize="small"
                  />
                  {company.address?.distance !== undefined &&
                    company.address?.distance !== null && (
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "2px 10px",
                          borderRadius: "20px",
                          fontSize: "11px",
                          fontWeight: 600,
                          letterSpacing: "0.02em",
                          background: distanceGreenBg,
                          color: distanceGreen,
                          border: `1px solid ${distanceGreen}22`,
                          whiteSpace: "nowrap",
                          fontFamily: dt.fontMono,
                        }}
                      >
                        {company.address.distance < 1
                          ? "< 1 km"
                          : company.address.distance >= 1000
                          ? `${(company.address.distance / 1000).toFixed(0)}k km`
                          : `${Math.round(company.address.distance)} km`}
                      </Box>
                    )}
                  <Box
                    sx={{
                      width: 3,
                      height: 3,
                      borderRadius: "50%",
                      background: dt.textMuted,
                      flexShrink: 0,
                    }}
                  />
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "3px 11px",
                      borderRadius: "20px",
                      fontSize: "11px",
                      fontWeight: 600,
                      letterSpacing: "0.02em",
                      background: roleBg,
                      color: roleColor,
                      border: `1px solid ${roleColor}22`,
                      whiteSpace: "nowrap",
                      fontFamily: dt.font,
                    }}
                  >
                    {roleLabel}
                  </Box>
                </Box>

                {/* Phone: inline action buttons below name */}
                {isPhone && <ActionButtons />}
              </Box>
            </Box>

            {/* Row 2: Tier 2 badges (Partnership + Groups) — below identity */}
            {(partnershipStatus?.type === "partner" ||
              (matchingGroups && matchingGroups.length > 0)) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                  px: isPhone ? "16px" : "28px",
                  pt: "12px",
                }}
              >
                {/* Partnership badge */}
                {partnershipStatus?.type === "partner" && partnershipStatus.tier && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <TierBadge tier={partnershipStatus.tier} size="medium" />
                    <Box
                      sx={{
                        fontSize: "11px",
                        color: dt.textMuted,
                        fontFamily: dt.fontMono,
                      }}
                    >
                      {translate("partnerships:partner.since")}{" "}
                      {dayjs(partnershipStatus.activatedAt).format("DD MMM YYYY")}
                    </Box>
                  </Box>
                )}
                {/* Group badges */}
                {matchingGroups?.map((group) => (
                  <Box
                    key={`grp-${group.id}`}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "3px 11px",
                      borderRadius: "20px",
                      fontSize: "11px",
                      fontWeight: 600,
                      letterSpacing: "0.02em",
                      background: `${dt.amber}14`,
                      color: dt.amber,
                      border: `1px solid ${dt.amber}22`,
                      whiteSpace: "nowrap",
                      fontFamily: dt.font,
                    }}
                  >
                    {group.name}
                  </Box>
                ))}
              </Box>
            )}

            {/* Row 3: Default contact */}
            {company.defaultContact && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  px: isPhone ? "16px" : "28px",
                  pt: "12px",
                  flexWrap: "wrap",
                }}
              >
                <PersonOutlinedIcon
                  sx={{ fontSize: 15, color: dt.textMuted }}
                />
                <Box
                  sx={{
                    fontSize: "13px",
                    color: dt.textSecondary,
                    fontFamily: dt.font,
                    fontWeight: 600,
                  }}
                >
                  {company.defaultContact.name}
                  {company.defaultContact.position &&
                    ` \u00B7 ${company.defaultContact.position}`}
                </Box>
                {company.defaultContact.phoneNumber && (
                  <Box
                    component="a"
                    href={`tel:${company.defaultContact.phoneNumber}`}
                    sx={{
                      fontSize: "12px",
                      color: dt.textLink,
                      textDecoration: "none",
                      fontFamily: dt.font,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {company.defaultContact.phoneNumber}
                  </Box>
                )}
                {company.defaultContact.email && (
                  <Box
                    component="a"
                    href={`mailto:${company.defaultContact.email}`}
                    sx={{
                      fontSize: "12px",
                      color: dt.textLink,
                      textDecoration: "none",
                      fontFamily: dt.font,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {company.defaultContact.email}
                  </Box>
                )}
              </Box>
            )}

            {/* Row 4: Description (subtle top border) */}
            {(isEditing || company.description) && (
              <Box
                sx={{
                  px: isPhone ? "16px" : "28px",
                  pb: "16px",
                  pt: "16px",
                  mt: "12px",
                  borderTop: `1px solid ${borderLight}`,
                }}
              >
                {isEditing && editForm && onFieldChange ? (
                  <textarea
                    value={editForm.description}
                    onChange={(e) => onFieldChange("description", e.target.value)}
                    placeholder={translate("partnerHub:edit.descriptionPlaceholder")}
                    rows={4}
                    style={{
                      ...inputStyle,
                      resize: "vertical",
                      lineHeight: "1.7",
                      minHeight: "100px",
                    }}
                  />
                ) : (
                  <CompanyDescriptionInline text={company.description || ""} />
                )}
              </Box>
            )}

            {/* Bottom padding if no description */}
            {!isEditing && !company.description && (
              <Box sx={{ pb: "16px" }} />
            )}
          </Box>

          {/* ===================== RIGHT PANEL (desktop only) ===================== */}
          {isDesktop && (
            <Box
              sx={{
                width: "350px",
                flexShrink: 0,
                borderLeft: `1px solid ${dt.cardBorder}`,
                padding: "24px 24px 20px",
              }}
            >
              <ContactPanelContent />
            </Box>
          )}
        </Box>

        {/* --- Contact section (tablet: always visible, phone: collapsible) --- */}
        {!isDesktop && (
          <Box
            sx={{
              borderTop: `1px solid ${borderLight}`,
              px: isPhone ? "16px" : "28px",
              py: "16px",
            }}
          >
            {isPhone ? (
              <CollapsibleSection
                title={translate("partnerHub:hero.contactInfo")}
                expanded={contactExpanded}
                onToggle={() => setContactExpanded((v) => !v)}
              >
                <ContactPanelContent />
              </CollapsibleSection>
            ) : (
              <ContactPanelContent />
            )}
          </Box>
        )}

        {/* --- Social Links Bar (full width, spans both columns) --- */}
        {hasSocialLinks && (
          <Box
            sx={{
              borderTop: `1px solid ${borderLight}`,
              px: isPhone ? "16px" : "28px",
              py: "12px",
              display: "flex",
              alignItems: "center",
              gap: isEditing ? "12px" : "6px",
              flexWrap: "wrap",
            }}
          >
            {isEditing && editForm && onFieldChange
              ? socialLinks.map((link, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flex: isPhone ? "1 1 100%" : "0 1 auto",
                      minWidth: isPhone ? "100%" : "180px",
                    }}
                  >
                    <Box sx={{ mr: "8px", flexShrink: 0 }}>{link.icon}</Box>
                    {link.prefix ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flex: 1,
                          borderRadius: dt.radiusSm,
                          border: `1px solid ${dt.cardBorder}`,
                          overflow: "hidden",
                          background: "#F8FAFC",
                        }}
                      >
                        <Box
                          sx={{
                            px: "8px",
                            fontSize: "11px",
                            color: dt.textMuted,
                            fontFamily: dt.font,
                            whiteSpace: "nowrap",
                            borderRight: `1px solid ${dt.cardBorder}`,
                            lineHeight: "34px",
                            background: dt.inputBg,
                            userSelect: "none",
                          }}
                        >
                          {link.prefix}
                        </Box>
                        <input
                          value={(editForm[link.editField!] as string) || ""}
                          onChange={(e) =>
                            onFieldChange(link.editField!, e.target.value)
                          }
                          placeholder={link.placeholder || ""}
                          style={{
                            ...inputStyle,
                            border: "none",
                            borderRadius: 0,
                            flex: 1,
                            background: "transparent",
                          }}
                        />
                      </Box>
                    ) : (
                      <input
                        value={(editForm[link.editField!] as string) || ""}
                        onChange={(e) =>
                          onFieldChange(link.editField!, e.target.value)
                        }
                        placeholder={link.placeholder || link.tooltip}
                        style={{ ...inputStyle, flex: 1 }}
                      />
                    )}
                  </Box>
                ))
              : socialLinks
                  .filter((l) => !!l.value)
                  .map((link, idx) => (
                    <Tooltip key={idx} title={link.tooltip} arrow>
                      <Box
                        component="a"
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: dt.radiusSm,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: `1px solid ${dt.cardBorder}`,
                          background: dt.cardBg,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          textDecoration: "none",
                          "&:hover": {
                            borderColor: dt.blue,
                            boxShadow: `0 0 0 3px ${dt.blueSoft}`,
                          },
                        }}
                      >
                        {link.icon}
                      </Box>
                    </Tooltip>
                  ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// =====================================================================
//  CompanyDescriptionInline (4-line truncation)
// =====================================================================

const CompanyDescriptionInline = ({ text }: { text: string }) => {
  const [expanded, setExpanded] = useState(false);
  const { translate } = useTranslation("partnerHub");

  if (!text) return null;

  return (
    <Box>
      <Box
        sx={{
          color: dt.textSecondary,
          fontSize: "14px",
          lineHeight: 1.7,
          fontFamily: dt.font,
          ...(expanded
            ? {}
            : {
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }),
        }}
      >
        {text}
      </Box>
      {text.length > 300 && (
        <Box
          onClick={() => setExpanded((v) => !v)}
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            color: dt.blue,
            cursor: "pointer",
            mt: "4px",
            fontFamily: dt.font,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {expanded
            ? translate("hero.showLess")
            : translate("hero.readMore")}
        </Box>
      )}
    </Box>
  );
};

export default CompanyHero;
